import React, { useState } from "react";
import { X, ShieldCheck, Warning, CurrencyEur } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EMPTY = {
  title: "",
  opportunity_type: "bank_owned",
  source_type: "bank",
  bank_or_creditor: "",
  external_reference: "",
  country: "Portugal",
  district: "",
  city: "",
  address: "",
  base_price: "",
  minimum_price: "",
  estimated_market_value: "",
  estimated_discount_percent: "",
  deadline: "",
  occupancy_status: "unknown",
  description: "",
  investment_case: "",
  risk_notes: "",
  verification_status: "unverified",
  verification_notes: "",
  mandate_type: "none",
  paying_party: "",
  fee_type: "percentage",
  fee_value: "",
  commission_status: "unprotected",
  commission_notes: "",
  record_status: "draft",
  confidentiality: "internal",
  show_price: true,
  show_address: false,
  show_source: false,
  show_process: true,
  show_analysis: true,
};

const numericFields = [
  "base_price", "minimum_price", "estimated_market_value",
  "estimated_discount_percent", "fee_value",
];

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1.5">
        {label}{required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full h-11 border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]";

export default function BankOpportunityForm({ initial, meta, onSave, onClose, saving }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });

  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = (event) => {
    event.preventDefault();
    const payload = { ...form };
    numericFields.forEach((field) => {
      payload[field] = payload[field] === "" ? null : Number(payload[field]);
    });
    onSave(payload);
  };

  const select = (field, values) => (
    <select
      value={form[field] || ""}
      onChange={(event) => set(field, event.target.value)}
      className={inputClass}
    >
      {(values || []).map((value) => (
        <option key={value} value={value}>
          {t(`bankOpportunities.form.options.${value}`, String(value).replaceAll("_", " "))}
        </option>
      ))}
    </select>
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex justify-center p-3 sm:p-6">
      <div className="w-full max-w-5xl bg-[#F8F9FA] shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[#0B2545] text-white px-6 py-5 flex items-start justify-between">
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-[#C5A059] mb-1">
              Panatlantik Investment Intelligence
            </p>
            <h2 className="font-serif text-2xl">
              {initial
                ? t("bankOpportunities.form.edit")
                : t("bankOpportunities.add")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-300 hover:text-white"
            aria-label={t("bankOpportunities.form.close")}
          >
            <X size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 sm:p-7 space-y-7">
          <section className="bg-white border border-slate-200 p-5">
            <h3 className="font-serif text-lg text-[#0B2545] mb-4">
              {t("bankOpportunities.form.identification")}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Field label={t("bankOpportunities.form.opportunityTitle")} required>
                  <Input
                    value={form.title}
                    onChange={(event) => set("title", event.target.value)}
                    required
                    className="rounded-none h-11"
                  />
                </Field>
              </div>
              <Field label={t("bankOpportunities.form.opportunityType")}>
                {select("opportunity_type", meta?.opportunity_types)}
              </Field>
              <Field label={t("bankOpportunities.form.recordStatus")}>
                {select("record_status", meta?.record_statuses)}
              </Field>
              <Field label={t("bankOpportunities.form.sourceType")}>
                {select("source_type", meta?.source_types)}
              </Field>
              <Field label={t("bankOpportunities.form.bankCreditor")}>
                <Input
                  value={form.bank_or_creditor}
                  onChange={(event) => set("bank_or_creditor", event.target.value)}
                  className="rounded-none h-11"
                />
              </Field>
              <Field label={t("bankOpportunities.form.externalReference")}>
                <Input
                  value={form.external_reference}
                  onChange={(event) => set("external_reference", event.target.value)}
                  className="rounded-none h-11"
                />
              </Field>
              <Field label={t("bankOpportunities.form.deadline")}>
                <Input
                  type="date"
                  value={form.deadline || ""}
                  onChange={(event) => set("deadline", event.target.value)}
                  className="rounded-none h-11"
                />
              </Field>
            </div>
          </section>

          <section className="bg-white border border-slate-200 p-5">
            <h3 className="font-serif text-lg text-[#0B2545] mb-4">
              {t("bankOpportunities.form.location")}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["country", "Country"],
                ["district", "District"],
                ["city", "City"],
                ["address", "Address"],
              ].map(([field, fallback]) => (
                <Field key={field} label={t(`bankOpportunities.form.${field}`)}>
                  <Input
                    value={form[field] || ""}
                    onChange={(event) => set(field, event.target.value)}
                    className="rounded-none h-11"
                  />
                </Field>
              ))}
            </div>
          </section>

          <section className="bg-white border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <CurrencyEur size={20} className="text-[#C5A059]" />
              <h3 className="font-serif text-lg text-[#0B2545]">
                {t("bankOpportunities.form.financial")}
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["base_price", "Base price"],
                ["minimum_price", "Minimum price"],
                ["estimated_market_value", "Estimated market value"],
                ["estimated_discount_percent", "Estimated discount %"],
              ].map(([field, fallback]) => (
                <Field key={field} label={t(`bankOpportunities.form.${field}`)}>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    value={form[field] ?? ""}
                    onChange={(event) => set(field, event.target.value)}
                    className="rounded-none h-11"
                  />
                </Field>
              ))}
            </div>
          </section>

          <section className="bg-white border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Warning size={20} className="text-[#C5A059]" />
              <h3 className="font-serif text-lg text-[#0B2545]">
                {t("bankOpportunities.form.analysisDueDiligence")}
              </h3>
            </div>
            <div className="space-y-4">
              {[
                ["description", "Description"],
                ["investment_case", "Investment case"],
                ["risk_notes", "Risks and matters to confirm"],
              ].map(([field, fallback]) => (
                <Field key={field} label={t(`bankOpportunities.form.${field}`)}>
                  <textarea
                    value={form[field] || ""}
                    onChange={(event) => set(field, event.target.value)}
                    rows={3}
                    className={`${inputClass} h-auto py-3 resize-y`}
                  />
                </Field>
              ))}
              <Field label={t("bankOpportunities.form.verification")}>
                {select("verification_status", meta?.verification_statuses)}
              </Field>
            </div>
          </section>

          <section className="bg-white border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck size={20} className="text-[#C5A059]" />
              <h3 className="font-serif text-lg text-[#0B2545]">
                {t("bankOpportunities.form.commissionProtection")}
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Field label={t("bankOpportunities.form.mandateType")}>
                {select("mandate_type", meta?.mandate_types)}
              </Field>
              <Field label={t("bankOpportunities.form.commissionStatus")}>
                {select("commission_status", meta?.commission_statuses)}
              </Field>
              <Field label={t("bankOpportunities.form.payingParty")}>
                <Input
                  value={form.paying_party}
                  onChange={(event) => set("paying_party", event.target.value)}
                  className="rounded-none h-11"
                />
              </Field>
              <Field label={t("bankOpportunities.form.feeValue")}>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={form.fee_value ?? ""}
                  onChange={(event) => set("fee_value", event.target.value)}
                  className="rounded-none h-11"
                />
              </Field>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row sm:justify-between gap-3 border-t border-slate-200 pt-5">
            <p className="text-xs text-slate-500 max-w-xl">
              {t("bankOpportunities.notice")}
            </p>
            <div className="flex gap-3 sm:flex-shrink-0">
              <Button type="button" variant="outline" onClick={onClose} className="rounded-none">
                {t("bankOpportunities.form.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-none bg-[#0B2545] hover:bg-[#12345e]"
              >
                {saving ? t("bankOpportunities.form.saving") : t("bankOpportunities.form.save")}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
